import React from 'react';
import { Card } from 'react-bootstrap';
import AdminSetupLink from './AdminSetupLink';
import './headerCard.css';

type HeaderCardProps = {
  supportLink: string;
  tenant: number;
  /**
   * One line instead of a card.
   *
   * Off by default, because this component is shared with `PqlPage` and `MqaPage` and
   * those render on routes no flag covers. Only the datasets dialog asks for it, and
   * the dialog is reachable only from flag-gated surfaces.
   */
  compact?: boolean;
};

/**
 * What these two datasets are, in one line, above the form that builds them.
 *
 * It was a full card: a four-line paragraph and a book icon under it, taking the top
 * third of a dialog before a single control. The sentence it carried is worth keeping
 * ("the validation dataset shouldn't overlap the training one" is the rule people get
 * wrong), so it is kept and the card around it is not.
 *
 * The two mapping links go where the app's own sidebar sends you, which is a flag the
 * admin app owns (see `AdminSetupLink`). They were fixed to `/audiences` and
 * `/conversions`, which redirect to the org home for a tenant without the flag, and
 * before that they pointed at `/mapping/audiences` and, for conversions, the
 * LEGACY admin's `/mapping/conversion_mapping/overview`: superseded pages rather than
 * typos, which is how they survived and how this app came to hold two answers to where a
 * tenant's audiences are configured.
 */
/**
 * The card as it was, which is what every unflagged route still renders.
 *
 * Taken from master unchanged, including the two mapping links: correcting those is a
 * user-visible change to `PqlPage` and `MqaPage`, and this file is not the place to make
 * one without a flag. The compact version below has the current paths.
 */
const LegacyHeaderCard = ({ supportLink, tenant }: HeaderCardProps) => (
  <Card border="light">
    <Card.Body>
      <p>
        A model is trained using a training dataset then validated on a
        validation dataset which shouldn&apos;t overlap the training dataset.
        Build these datasets from audiences and conversions defined in the{' '}
        <AdminSetupLink
          tenant={tenant}
          page="audience"
          className="text-primary"
        >
          Audience Mapping
        </AdminSetupLink>{' '}
        and{' '}
        <AdminSetupLink
          tenant={tenant}
          page="conversion"
          className="text-primary"
        >
          Conversion Mapping
        </AdminSetupLink>
      </p>
      <a
        href={supportLink}
        target="_blank"
        rel="noopener noreferrer"
        className="text-decoration-none text-primary"
      >
        <i aria-hidden className="fas fa-book-open"></i> How to choose the
        datasets parameters
      </a>
    </Card.Body>
  </Card>
);

const HeaderCard = ({ supportLink, tenant, compact }: HeaderCardProps) => {
  if (!compact)
    return <LegacyHeaderCard supportLink={supportLink} tenant={tenant} />;
  return (
    <p className="hcard">
      Validation should not overlap training. Both are built from the audiences
      and conversions defined in{' '}
      <AdminSetupLink tenant={tenant} page="audience">
        Audiences
      </AdminSetupLink>{' '}
      and{' '}
      <AdminSetupLink tenant={tenant} page="conversion">
        Conversions
      </AdminSetupLink>
      .{' '}
      <a href={supportLink} target="_blank" rel="noopener noreferrer">
        How to choose these
      </a>
    </p>
  );
};

export default HeaderCard;
